import { fabric } from 'fabric';
import { useContext, useEffect, useMemo, useState } from 'react';

import { CANVAS_OBJECT_TYPE_PREFIX, CORNER_RADIUS_SIGN, DRAWING_SHAPE_COLOR_PALETTE } from '@common/constants';
import { DrawingShape } from '@common/models';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { DrawingShapeContext } from '@ee-context';
import { useAppSelector } from '@redux/hooks';
import { selectNeedRedrawCanvas } from '@redux/slices/drawingShapeSlice';
import { selectFabricCanvas } from '@redux/slices/fabricSlice';

import { generateCanvasJpeg } from '../canvas/drawing-tool/utils/drawing-helper';
import drawingTool from '../drawer/drawingTool';

const objectTypesPrefixes = [
    CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC,
    CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT,
    CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT,
] as const satisfies Readonly<string[]>;

const emptyArray = [] satisfies DrawingShape[];

/**Mutates canvas argument, removes selections */
const removeSelections = (canvas: fabric.Canvas, overlayColor: string): void => {
    // calling selectShape with activeDrawingShape undefined makes a canvas with no shapes selected.
    drawingTool.selectShape({
        allShapes: emptyArray,
        canvas,
        activeDrawingShape: undefined,
        color: overlayColor,
    });
    canvas.getObjects().forEach((object) => {
        if (
            object.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL) &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            object.text.length === 1 &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            !object.text.includes(CORNER_RADIUS_SIGN)
        ) {
            object.set({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                text: '',
            });
        } else if (object.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL)) {
            object.set({
                stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
                fill: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
            });
        }

        /**Case where object is a group */
        if ((object as fabric.Group).getObjects) {
            const objects = (object as fabric.Group).getObjects();
            objects.forEach((object) => {
                const shouldTurnStrokeGray = objectTypesPrefixes.some((prefix) => object?.name?.includes(prefix));
                if (shouldTurnStrokeGray) {
                    object.set({
                        stroke: DRAWING_SHAPE_COLOR_PALETTE.NORMAL.STROKE,
                    });
                }
            });
        }
    });
};

export const cloneCanvasAndRemoveSelections = (
    canvas: fabric.Canvas,
    overlayColor?: string
): Promise<fabric.Canvas> => {
    return new Promise((resolve) => {
        canvas?.clone?.(
            (canvasClone: fabric.Canvas) => {
                if (overlayColor) {
                    removeSelections(canvasClone, overlayColor);
                }
                resolve(canvasClone);
            },
            ['name', 'data'] //Without this array FabricJs drops this values
        );
    });
};

/**
 * Provides a canvas clone with the selected shape removed
 * @param stopUrlGeneration stops the url generation for performance in components that are hidden instead of unmounted
 */
export const useCanvasClone = (
    stopUrlGeneration: boolean = false
): {
    /**A canvas url with a JPEG generated or null if stopUrlGeneration set or the clone is not ready */
    canvasJpegUrl: string | null;
} => {
    const canvas = useAppSelector(selectFabricCanvas);
    const { selectedValue: selectedColor } = useSelectedColor();
    const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);
    const { drawingShapes } = useContext(DrawingShapeContext);

    const [canvasClone, setCanvasClone] = useState<Readonly<fabric.Canvas>>();

    useEffect(() => {
        //if stopUrlGeneration set no need to clone the canvas
        if (stopUrlGeneration || !selectedColor || !drawingTool.allShapesAndAppliancesAreDrawn(canvas, drawingShapes)) {
            return;
        }
        // Cloning canvas to modify it without modifying the origin canvas (to show all shapes as not selected)
        cloneCanvasAndRemoveSelections(canvas, selectedColor.overlayColor).then((clone) => {
            setCanvasClone(clone);
        });

        // Including needRedrawCanvas to force re-triggering of effect when canvas is mutated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, stopUrlGeneration, drawingShapes, needRedrawCanvas, selectedColor]);

    /**
     * A JPEG URL of the canvas with no shapes selected
     */
    const canvasJpegUrl = useMemo(() => {
        return stopUrlGeneration || !canvasClone ? null : generateCanvasJpeg(canvasClone);
    }, [canvasClone, stopUrlGeneration]);

    return { canvasJpegUrl };
};
